import moment from "moment";
import type { Response } from "superagent";
import request from "../../../util/SuperagentUtils";
import { DateFormats } from "../../../components/constants/securspace-constants";
import { buyerInvoicesReportEndpoints } from "../../api-endpoints";
import {getUrlSearchParamsByPaging} from "../../../util/UrlPagingParamUtils";

type BuyerInvoiceQueryParams = {
  page?: number;
  size?: number;
  startDate?: string;
  endDate?: string;
  locationId?: string;
  bookingNumber?: number;
  transactionNumber?: number;
  transactionType?: string;
  buyerTransactionStatus?: string;
  sortBy?: string;
  sortDir?: string;
}

const getBuyerInvoiceQueryParams = (searchParams: BuyerInvoiceQueryParams, isPageable: boolean) => {
  const { page, size, startDate, endDate, locationId, bookingNumber, transactionNumber, transactionType, buyerTransactionStatus, sortBy, sortDir } = searchParams;

  const queryParams = isPageable ? getUrlSearchParamsByPaging(page, size) : new URLSearchParams();

  if (startDate) queryParams.append("startDate", moment(startDate).format(DateFormats.DAY));
  if (endDate) queryParams.append("endDate", moment(endDate).format(DateFormats.DAY));
  if (locationId) queryParams.append("locationId", locationId);
  if (bookingNumber) queryParams.append("bookingNumber", String(bookingNumber));
  if (transactionNumber) queryParams.append("transactionNumber", String(transactionNumber));
  if (transactionType) queryParams.append("transactionType", transactionType);
  if (buyerTransactionStatus) queryParams.append("buyerTransactionStatus", buyerTransactionStatus);
  if (sortBy) queryParams.append("sortBy", sortBy);
  if (sortDir) queryParams.append("sortDir", sortDir);

  return queryParams;
}

const getBuyerInvoices = (
    searchParams: URLSearchParams,
    onSuccess: () => void,
    onFail: () => void
) => {
  const params = getBuyerInvoiceQueryParams(searchParams, true);

  const result = request.get(`${buyerInvoicesReportEndpoints.search}?${params}`);

  if (onSuccess && onFail) {
    result
      .then(onSuccess)
      .catch(onFail)
  } else {
    return result
  }
}

/**
 * Get the url for the buyer invoices report
 * @param buyerAccountId
 * @param searchParams
 * @returns {string}
 */
const getBuyerInvoicesReportUrl = (buyerAccountId: string, searchParams: BuyerInvoiceQueryParams): string => {
  const params = getBuyerInvoiceQueryParams(searchParams, true);

  return `${buyerInvoicesReportEndpoints.report}?${params.toString()}`;
};

const requestBuyerInvoicesSummary = (
  searchParams: BuyerInvoiceQueryParams,
  onSuccess?: (response: Response) => void,
  onFail?: (error: any) => void,
) => {
  const params = getBuyerInvoiceQueryParams(searchParams, true);

  const result = request.get(`${buyerInvoicesReportEndpoints.summary}?${params}`);

  // Always chain from the same promise so a single rejection path handles both
  // request failures and any exception thrown by onSuccess itself (matching
  // the semantics of `.then(onSuccess).catch(onFail)` rather than attaching
  // the two handlers independently, which would let an onSuccess error escape
  // unhandled).
  if (onSuccess && onFail) return result.then(onSuccess).catch(onFail);
  if (onSuccess) return result.then(onSuccess);
  if (onFail) return result.catch(onFail);

  return result;
}

export {
    getBuyerInvoices,
    getBuyerInvoicesReportUrl,
    requestBuyerInvoicesSummary
}
